import { FC, useEffect, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import { Helmet } from "react-helmet-async";
import IconChecksSvg from "../../assets/svg-fc/IconChecksSvg";
import NotificationCard from "../../components/reusable-components/notification-card/NotificationCard";
import NotificationFilters from "../../components/reusable-components/notification-filters/NotificationFilters";
import ScrollToTopButton from "../../components/reusable-components/scroll-to-top/ScrollToTopButton";
import {
	NotificationEmptyState,
	NotificationLoadError,
	NotificationRowsSkeleton,
} from "../../components/reusable-components/notification-states/NotificationStates";
import { useLoadMoreSentinel, useNotificationList } from "../../hooks/useNotificationList";
import { DEFAULT_NOTIFICATION_FILTER } from "../../store/notifications/notificationSlice";
import { NOTIFICATION_PAGE_LIMIT } from "../../store/notifications/notificationThunk";
import { groupNotificationsByDate } from "../../utils/notificationUtils";
import "./notification.scss";

const Notification: FC = () => {
	const contentRef = useRef<HTMLDivElement>(null);
	const {
		filter,
		status,
		rows,
		counts,
		unread,
		canLoadMore,
		load,
		changeFilter,
		loadMore,
		markRead,
		remove,
		markAllRead,
	} = useNotificationList("page", NOTIFICATION_PAGE_LIMIT);
	const sentinelRef = useLoadMoreSentinel(loadMore, canLoadMore);
	const groups = useMemo(() => groupNotificationsByDate(rows), [rows]);

	useEffect(() => {
		load(DEFAULT_NOTIFICATION_FILTER);
	}, []);

	const renderList = () => {
		if (status === "loading" || (status === "idle" && !rows.length)) {
			return (
				<div className="notification_page_card">
					<NotificationRowsSkeleton size="large" />
				</div>
			);
		}
		if (!rows.length) {
			return (
				<div className="notification_page_card">
					{status === "failed" ? (
						<NotificationLoadError onRetry={() => changeFilter(filter)} />
					) : (
						<NotificationEmptyState filter={filter} />
					)}
				</div>
			);
		}
		return groups.map(({ label, items }) => (
			<section key={label} className="notification_page_group">
				<h2>{label}</h2>
				<div className="notification_page_card">
					{items.map((row) => (
						<NotificationCard
							key={row.id}
							notification={row}
							size="large"
							onOpen={() => markRead(row)}
							onMarkRead={() => markRead(row)}
							onRemove={() => remove(row)}
						/>
					))}
				</div>
			</section>
		));
	};

	return (
		<>
			<Helmet>
				<title>Notifications</title>
			</Helmet>
			<Box className="content notification_content" ref={contentRef}>
				<div className="notification_page">
					<div className="notification_page_head">
						<div>
							<h1>Notifications</h1>
							<p>Your events, newest first.</p>
						</div>
						{unread > 0 && (
							<button type="button" className="notification_page_mark_all" onClick={markAllRead}>
								<IconChecksSvg />
								Mark all as read
							</button>
						)}
					</div>
					<NotificationFilters variant="page" filter={filter} counts={counts} onChange={changeFilter} />
					<div className="notification_page_list">
						{renderList()}
						<div ref={sentinelRef} />
						{status === "loadingMore" && (
							<div className="notification_page_card">
								<NotificationRowsSkeleton rows={2} size="large" />
							</div>
						)}
					</div>
				</div>
				<ScrollToTopButton containerRef={contentRef} />
			</Box>
		</>
	);
};
export default Notification;
